import { makeStyles } from '@material-ui/core/styles';

// Untrusted values and proper nouns (client name, domain, account, brand):
// the theme lowercases h6, FormLabel and fullWidth buttons, and a long value
// without spaces must wrap instead of overflowing at 360 px.
const literalText = { textTransform: 'none', overflowWrap: 'anywhere', wordBreak: 'break-word' };

// MUI v4 gives contained/outlined buttons and checkboxes no 3:1 focus
// indicator, so every interactive element of the screen gets this ring.
const focusRing = (theme) => ({
  '&.Mui-focusVisible, &:focus-visible': { outline: `3px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
});

export const useConsentStyles = makeStyles((theme) => ({
  // Top aligned and horizontally centered, never vertically centered: with
  // zoom or a virtual keyboard the top of the card would leave the screen.
  // The page is the app header green (the logo's white dots disappear on
  // white); everything the user reads or focuses stays inside the white card.
  page: { background: theme.palette.primary.main, minHeight: '100vh', padding: theme.spacing(3, 2) },
  card: {
    maxWidth: 480,
    margin: '0 auto',
    padding: theme.spacing(2),
    [theme.breakpoints.only('xs')]: { boxShadow: 'none', border: `1px solid ${theme.palette.divider}` },
    [theme.breakpoints.up('sm')]: { padding: theme.spacing(3) },
  },
  logo: { display: 'block', margin: `0 auto ${theme.spacing(3)}px`, maxWidth: 'min(200px, 60%)', height: 'auto' },
  literal: literalText,
  domainBox: { padding: 12, borderRadius: 8, backgroundColor: 'rgba(0,0,0,0.04)' },
  domain: { ...literalText, fontWeight: 700, color: theme.palette.text.primary },
  account: { margin: theme.spacing(2, 0) },
  link: {
    ...literalText,
    color: theme.palette.primary.main,
    fontWeight: 700,
    textDecoration: 'underline',
    minHeight: 44,
    display: 'inline-flex',
    alignItems: 'center',
    textAlign: 'left',
    ...focusRing(theme),
  },
  row: {
    minHeight: 48,
    alignItems: 'flex-start',
    margin: 0,
    paddingTop: 4,
    paddingBottom: 4,
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create('background-color', { duration: theme.transitions.duration.short }),
    '&:hover': { backgroundColor: theme.palette.action.hover, '@media (hover: none)': { backgroundColor: 'transparent' } },
    '&.Mui-disabled:hover': { backgroundColor: 'transparent', cursor: 'default' },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    '& .MuiCheckbox-root.Mui-focusVisible, & .MuiCheckbox-root:focus-within': {
      outline: `3px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
      borderRadius: '50%',
    },
  },
  badge: {
    display: 'inline-block',
    marginTop: 2,
    padding: '0 8px',
    borderRadius: 4,
    textTransform: 'none',
    border: `1px solid ${theme.palette.text.secondary}`,
    color: theme.palette.text.primary,
    fontWeight: 600,
    whiteSpace: 'normal',
  },
  unverifiedHint: { marginTop: 4, color: theme.palette.text.primary, overflowWrap: 'anywhere' },
  alert: { marginTop: theme.spacing(2), overflowWrap: 'anywhere' },
  actions: {
    display: 'flex',
    flexDirection: 'column', // xs: stacked, Allow on top (DOM order Allow, Deny)
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    '& > *': { width: '100%' },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row-reverse', // sm+: Deny on the left, focus order stays Allow -> Deny
      justifyContent: 'flex-start',
      '& > *': { width: 'auto' },
    },
  },
  // Lowercase at every width (MUI's default is uppercase unless fullWidth).
  btn: { minHeight: 44, textTransform: 'lowercase', ...focusRing(theme) },
  deny: { color: theme.palette.text.primary, borderColor: theme.palette.text.secondary },
  // Revoke a connected app: error.dark (4.98:1 on white), never the green.
  revoke: {
    minHeight: 44,
    textTransform: 'lowercase',
    color: theme.palette.error.dark,
    borderColor: theme.palette.error.dark,
    ...focusRing(theme),
  },
  spinner: { marginRight: 8 },
  help: { marginTop: 8 },
  loading: { textAlign: 'center', padding: 24 },
  exitLink: { marginTop: 8 },
}));
